import { inject } from '@angular/core';
import { PvPerformanceResponse } from '@core/models';
import { InsightsService } from '@core/services/insights.service';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';

type PvPerformanceState = {
  isLoading: boolean;
  performance: PvPerformanceResponse | null;
  // The response carries no device id, and this store is root-provided, so without tracking which
  // device the document belongs to a consumer cannot tell a loaded document from the previous
  // device's one. The card's visibility depends on that distinction.
  deviceId: string | null;
};

const initialState: PvPerformanceState = {
  isLoading: false,
  performance: null,
  deviceId: null,
};

/**
 * The performance card's own slice, deliberately not part of TopologicalDeviceTelemetryStore:
 * that store carries one `isLoadingDeviceTelemetry` shared by every card on the insights page,
 * and this request is keyed on the anchor *day* rather than on the chart's range.
 */
export const PvPerformanceStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store, insightsService = inject(InsightsService)) => ({
    // Any failure resolves to `performance: null`, which the card renders as "not enough data".
    // That covers the expected refusals as well as the unexpected ones: the endpoint rejects a
    // device whose meter role is not 'production', and a device without ratio documents yet is
    // indistinguishable from one whose window is too short - in both cases there is nothing to
    // show, and an error state next to the other cards would overstate what went wrong.
    getPvPerformance: rxMethod<{ deviceId: string; day: Date }>(
      pipe(
        // Switching device drops the previous document straight away; switching period on the same
        // device keeps it, so the card does not blink back to "not enough data" while refetching.
        tap(({ deviceId }) =>
          patchState(store, {
            isLoading: true,
            ...(store.deviceId() === deviceId ? {} : { deviceId, performance: null }),
          }),
        ),
        switchMap(({ deviceId, day }) =>
          insightsService.getDevicePvPerformance(deviceId, day).pipe(
            tapResponse({
              next: (performance: PvPerformanceResponse) =>
                patchState(store, { performance, deviceId, isLoading: false }),
              error: () => patchState(store, { performance: null, deviceId, isLoading: false }),
            }),
          ),
        ),
      ),
    ),
  })),
);
